import { createContext, useContext, useState } from "react";

/* Note: This uses terms similar to Jotai since it replaced the Jotai npm package. */

type Atom<T> = {
  key: symbol;
  initialValue: T;
};

type State = Map<symbol, unknown>;

const StateContext = createContext<State | undefined>(undefined);
const SetStateContext = createContext<
  React.Dispatch<React.SetStateAction<State>> | undefined
>(undefined);

export const Provider = ({ children }: { children: React.ReactNode }) => {
  const [state, setState] = useState<State>(new Map());
  return (
    <StateContext.Provider value={state}>
      <SetStateContext.Provider value={setState}>
        {children}
      </SetStateContext.Provider>
    </StateContext.Provider>
  );
};

export const atom = <T,>(initialValue: T): Atom<T> => {
  const key = Symbol();
  return { key, initialValue };
};

const getAtomValue = <T,>(state: State, atom: Atom<T>): T =>
  (state.get(atom.key) as T) ?? atom.initialValue;

const createAtomSetter =
  <T,>(setState: React.Dispatch<React.SetStateAction<State>>, atom: Atom<T>) =>
  (newValue: React.SetStateAction<T>) => {
    setState((prevState) => {
      const newState = new Map(prevState);
      const prevValue = (newState.get(atom.key) as T) ?? atom.initialValue;
      newState.set(
        atom.key,
        typeof newValue === "function"
          ? (newValue as (prev: T) => T)(prevValue)
          : newValue,
      );
      return newState;
    });
  };

export const useAtom = <T,>(
  atom: Atom<T>,
): [T, React.Dispatch<React.SetStateAction<T>>] => {
  const state = useContext(StateContext);
  const setState = useContext(SetStateContext);
  if (state === undefined || setState === undefined) {
    throw new Error("useAtom must be used within a Provider");
  }
  const value = getAtomValue(state, atom);
  const setValue = createAtomSetter(setState, atom);
  return [value, setValue];
};

export const useSetAtom = <T,>(
  atom: Atom<T>,
): React.Dispatch<React.SetStateAction<T>> => {
  const setState = useContext(SetStateContext);
  if (setState === undefined) {
    throw new Error("useSetAtom must be used within a Provider");
  }
  return createAtomSetter(setState, atom);
};

export const useAtomValue = <T,>(atom: Atom<T>): T => {
  const state = useContext(StateContext);
  if (state === undefined) {
    throw new Error("useAtomValue must be used within a Provider");
  }
  return getAtomValue(state, atom);
};
